import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import notifications from "../../../common/Components/Notifications/Notification";
import { HKPImage } from "../../../media/HKPImage";
import { applyDraftOverlay, masterIdFromRef, masterNameFromRef, publishProductDraft } from "../../../uploads/productDraftApi";
import { isTypeTagsEnabled } from "../../../helpers/typeTagsEnabled";
import { hasReturnMarker, returnTrailFor } from "../../../helpers/returnTrail";
import { isProductDraftRevisionsEnabled } from "../../../uploads/productDraftRevisionsEnabled";
import { studioPieceQrUrl } from "../../sales/internalQr";
import { PlSheet, StockSwitch, useProductListLayout } from "../list/ProductListBits";
import { PlIcon } from "../list/plIcons";
import { formatRupee, previewPath, productBadge } from "../list/productListRow";
import { filterPriceInput, normalizePriceInput } from "../productListPriceInput";
import { previewMediaUrl } from "../previewMedia";
import { getFlexibleFirstProcesses } from "../productProcessMigrationUtils";
import ProductQrBindingPanel from "../ProductQrBindingPanel";
import { isDraftStatus } from "../productStockDisplay";
import CategoryPathPicker from "../steps/CategoryPathPicker";
import PublishChangesSheet from "../steps/PublishChangesSheet";
import TypeTagPicker from "../steps/TypeTagPicker";
import {
  coverRowIndex,
  coverageOf,
  coverageSummary,
  isShownRow,
  isVideoRow,
  normalizeSkipped,
  orderMediaByView,
  slotOf,
  VIEW_LABEL,
  viewSlotsFor,
} from "../steps/mediaViews";
import { draftChanges } from "../steps/productDraftDiff";
import "../productList.scss";
import "../photoUpdate/photoUpdate.scss";
import PhotoUpdatePanel from "../photoUpdate/PhotoUpdatePanel";
import ShadePhotosPanel from "../shade/ShadePhotosPanel";
import "./view-product.scss";
import FieldRow from "./FieldRow";
import ProductLabelStrip from "../../physical-identity/ProductLabelStrip";
import VpPop from "./VpPop";
import { fetchProductForView, patchProductFields, saveProductPrices, saveProductStock } from "./viewProductApi";
import {
  applyPatchResult,
  BASIC_FIELDS,
  categoryPathText,
  detailGroupsFor,
  displayValue,
  FieldSpec,
  filledCount,
  NAME_FIELD,
  patchBodyFor,
  pieceSn,
  isChanged,
  rawValue,
} from "./viewProductFields";

type SheetKind = null | "more" | "category" | "typeTags" | "price";

const rowUrl = (row: any): string => previewMediaUrl(row);
const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
const rectOf = (event?: React.MouseEvent<HTMLElement>): DOMRect | null => event?.currentTarget?.getBoundingClientRect?.() ?? null;

/**
 * View product (Figma 📱 Phone v4 V01–V12, 📲 Tablet v4 VT01–VT09, 💻 Laptop v4 VL01–VL09). Opened from a product card or row.
 * The view is the editor: every row has a pencil, Save writes only that field (PATCH /product/{id}/fields).
 * Stock keeps its live endpoint; prices keep theirs; photos and making steps open the step-by-step editor.
 * Phone stacks everything with Details and Making as accordions and pickers as bottom sheets. Tablet and laptop
 * show photos on the left, every section open, pickers as popovers by the row and Publish in the top bar.
 */
const ViewProductPage = () => {
  const history = useHistory();
  const location = useLocation<any>();
  const { id } = useParams<{ id: string }>();
  // Opened from a camera scan of the piece's HKP label: show its stage strip (US-005).
  const labelToken = new URLSearchParams(location.search).get("label") || "";
  const draftRevisionsEnabled = isProductDraftRevisionsEnabled();
  const layout = useProductListLayout();
  const wide = layout !== "phone";
  const laptop = layout === "laptop";
  const typeTagsOn = isTypeTagsEnabled();

  // The card the user tapped seeds the header, so the photo and name show before the fetch returns (V12).
  const [raw, setRaw] = useState<any | null>(location.state?.productDetails ?? null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [makingOpen, setMakingOpen] = useState(false);
  const [shadeKey, setShadeKey] = useState(0);
  const [coverFlash, setCoverFlash] = useState(false);
  const [sheet, setSheet] = useState<SheetKind>(null);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [typeTagIds, setTypeTagIds] = useState<string[]>([]);
  const [price, setPrice] = useState({ sell: "", cost: "" });
  const [priceBusy, setPriceBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const doc = await fetchProductForView(id);
      if (!doc) {
        setLoadError("This piece is not in your studio.");
      } else {
        setRaw(doc);
      }
    } catch (e: any) {
      setLoadError(e?.message || "Could not load the piece.");
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => {
    void load();
  }, [load]);

  const view = useMemo(() => (raw ? applyDraftOverlay(raw) : null), [raw]);
  const isDraft = isDraftStatus(raw?.status);
  const changes = useMemo(() => (raw && draftRevisionsEnabled ? draftChanges(raw) : []), [raw, draftRevisionsEnabled]);
  const editsPending = draftRevisionsEnabled && !isDraft && Boolean(raw?.hasDraftChanges) && changes.length > 0;
  const badge = raw ? productBadge(raw, draftRevisionsEnabled) : null;

  const goBack = () => {
    // Came from the list, or from a page that left a trail (Karkhana): that page is the entry behind.
    if ((location.state?.productDetails || hasReturnMarker(location.state)) && history.length > 1) history.goBack();
    else {
      // Opened directly or reloaded with a return trail (a costing): go back there, not to the list.
      const trail = returnTrailFor(location.pathname);
      history.push(trail ? trail.to : "/products");
    }
  };
  const openEditor = (step?: string) =>
    history.push(`/edit-product/${encodeURIComponent(id)}${step ? `?step=${step}` : ""}`, { productDetails: view });
  const openPreview = () => history.push(previewPath(view), { productDetails: view });

  /** The one save path: PATCH with exactly `body`, then merge the answer into the piece on screen. */
  const saveFields = useCallback(
    async (body: Record<string, unknown>) => {
      if (!raw?._id) throw new Error("The piece is still loading.");
      const res = await patchProductFields(raw._id, body);
      if (!res?.success) throw new Error(res?.message || "Could not save.");
      const data = res.data || {};
      setRaw((prev: any) => applyPatchResult(prev, body, data));
      notifications({
        title: "Saved",
        message: data.saved === "draft" ? "Kept as a draft edit until you publish." : "Saved.",
        type: "success",
      });
      return data;
    },
    [raw?._id]
  );
  const rowSaver = (spec: FieldSpec) => (text: string) => saveFields(patchBodyFor(spec, text)).then(() => undefined);

  // The backend mirrors stock into draft.inStock, and the view is draft-overlaid, so both copies move together.
  const withStock = (prev: any, inStock: boolean) => ({
    ...prev,
    inStock,
    ...(prev?.draft && typeof prev.draft === "object" && "inStock" in prev.draft ? { draft: { ...prev.draft, inStock } } : {}),
  });
  const onStockToggle = async (_product: any, inStock: boolean) => {
    const before = Boolean(view?.inStock);
    setRaw((prev: any) => withStock(prev, inStock));
    const res = await saveProductStock(raw._id, inStock).catch(() => null);
    if (!res?.success) {
      setRaw((prev: any) => withStock(prev, before));
      notifications({ title: "Error", message: res?.message || "Could not change stock.", type: "danger" });
    }
  };

  // ── pickers: a bottom sheet on phone, a popover by the opener on tablet and laptop ──
  const openSheet = (kind: SheetKind, at: DOMRect | null = null) => {
    if (kind === "typeTags") setTypeTagIds(Array.isArray(view?.typeTagIds) ? view.typeTagIds : []);
    setAnchor(at);
    setSheet(kind);
  };
  const closeSheet = () => setSheet(null);
  const host = (kind: SheetKind, label: string, width: number, children: (close: () => void) => React.ReactNode) =>
    wide ? (
      <VpPop open={sheet === kind} anchor={anchor} label={label} width={width} onClose={closeSheet}>
        {children}
      </VpPop>
    ) : (
      <PlSheet open={sheet === kind} onClose={closeSheet} label={label}>
        {children}
      </PlSheet>
    );

  const openPrice = (event?: React.MouseEvent<HTMLElement>) => {
    setPrice({ sell: String(view?.sellPrice ?? ""), cost: String(view?.costPrice ?? "") });
    openSheet("price", rectOf(event));
  };
  const savePrice = async (close: () => void) => {
    setPriceBusy(true);
    const res = await saveProductPrices(raw._id, normalizePriceInput(price.sell) ?? "", normalizePriceInput(price.cost) ?? "").catch(() => null);
    setPriceBusy(false);
    if (!res?.success) {
      notifications({ title: "Error", message: res?.message || "Could not save the price.", type: "danger" });
      return;
    }
    notifications({
      title: "Saved",
      message: view?.priceEditsRequirePublish ? "Price kept as a draft edit until you publish." : "Price updated.",
      type: "success",
    });
    close();
    await load();
  };

  const copyLink = async (close: () => void) => {
    const url = studioPieceQrUrl(id);
    let ok = false;
    try {
      await navigator.clipboard.writeText(url);
      ok = true;
    } catch {
      ok = false;
    }
    notifications({ title: ok ? "Copied" : "Error", message: ok ? "Product link copied." : "Could not copy the link.", type: ok ? "success" : "danger" });
    close();
  };

  const publish = async () => {
    setPublishing(true);
    const res = await publishProductDraft(raw._id).catch(() => null);
    setPublishing(false);
    if (!res?.success) {
      notifications({ title: "Not published", message: res?.message || "Could not publish.", type: "danger" });
      return;
    }
    setPublishOpen(false);
    notifications({ title: "Published", message: "Buyers, catalogues and the certificate now show the new version.", type: "success" });
    await load();
  };

  // ── photos ──
  const media: any[] = useMemo(() => orderMediaByView((view?.thumbnailDetails?.media || []).filter(isShownRow)), [view]);
  const photos = media.filter((r) => !isVideoRow(r));
  const cover = photos[Math.max(0, coverRowIndex(photos))];
  const slots = useMemo(() => viewSlotsFor({ type: view?.type, category: view?.category, subCategory: view?.subcategory, artform: view?.artform }), [view]);
  const coverage = useMemo(() => coverageOf(media, slots, normalizeSkipped(raw?.mediaViewsSkipped)), [media, slots, raw?.mediaViewsSkipped]);
  const firstOfView = (slot: string) => media.find((r) => slotOf(r) === slot);
  const missingViews = slots.filter((slot) => !firstOfView(slot) && !normalizeSkipped(raw?.mediaViewsSkipped).includes(slot));

  // ── sections ──
  const groups = useMemo(() => (view ? detailGroupsFor(view) : []), [view]);
  const detailFields = groups.flatMap((g) => g.fields);
  const detailsFilled = filledCount(view, detailFields);
  const steps = useMemo(() => (view ? getFlexibleFirstProcesses(view) : []), [view]);

  if (!view) {
    return (
      <div className={`pl-page vp-page is-${layout}`}>
        <div className="vp-head">
          <button type="button" className="pl-iconbtn" aria-label="Back" onClick={goBack}>
            <PlIcon name="back" />
          </button>
          <div className="vp-head__title">
            <b>Product</b>
            <small>{loading ? "Opening…" : ""}</small>
          </div>
        </div>
        {loading ? (
          <div className="vp-body" aria-busy="true" aria-label="Loading">
            <div className="vp-hero">
              <div className="vp-sk" style={{ width: wide ? 260 : 144, height: wide ? 421 : 233, borderRadius: 13 }} />
              <div className="vp-hero__side">
                <div className="vp-sk" style={{ height: 24, width: "90%" }} />
                <div className="vp-sk" style={{ height: 24, width: "60%" }} />
                <div className="vp-sk" style={{ height: 30, width: 120 }} />
                <div className="vp-sk" style={{ height: 30, width: 96 }} />
              </div>
            </div>
            {wide ? null : (
              <>
                <div className="vp-sk" style={{ height: 96, borderRadius: 13 }} />
                <div className="vp-sk" style={{ height: 180, borderRadius: 13 }} />
              </>
            )}
          </div>
        ) : (
          <div className="vp-state" role="alert">
            <span>{loadError || "Could not load the piece."}</span>
            <button type="button" className="pl-btn is-primary" onClick={() => void load()}>
              Try again
            </button>
            {labelToken ? (
              // No dead end after a scan: the label's own card, without redirecting back here.
              <button type="button" className="pl-btn" onClick={() => history.replace(`/q/${encodeURIComponent(labelToken)}?card=1`)}>
                Back to label
              </button>
            ) : null}
          </div>
        )}
      </div>
    );
  }

  const nameChanged = isChanged(raw, "name");
  const sub = [masterNameFromRef(view?.category) || masterNameFromRef(view?.type), badge?.label, loading ? "refreshing…" : ""].filter(Boolean).join(" · ");
  const editsLabel = `${changes.length} ${changes.length === 1 ? "edit" : "edits"}`;

  // ── pieces shared by every width ──
  const coverEl = cover ? (
    <button type="button" className={`vp-cover${coverFlash ? " is-flash" : ""}`} aria-label="Open photos" onClick={() => openEditor("photos")}>
      <HKPImage preset={laptop ? "CATALOGUE" : "PRODUCT_CARD"} legacyUrl={rowUrl(cover)} mediaId={cover?.mediaId} alt={view.name || "Product photo"} />
      <span className="vp-cover__cap">{slotOf(cover) ? `${VIEW_LABEL[slotOf(cover) as keyof typeof VIEW_LABEL]} · cover` : "Cover"}</span>
      <span className="vp-cover__cnt">1 / {media.length}</span>
    </button>
  ) : (
    <button type="button" className="vp-cover is-empty" onClick={() => openEditor("photos")}>
      <PlIcon name="plus" />
      Add photos
    </button>
  );

  const idsEl = (
    <div className="vp-ids">
      {view.xuid ? (
        <span className="vp-idchip">
          <PlIcon name="check" size={14} />
          HKP tag <code>{String(view.xuid)}</code>
        </span>
      ) : (
        <span className="vp-idchip is-muted">No HKP tag yet</span>
      )}
      <button type="button" className="vp-idchip is-blue" onClick={() => setQrOpen(true)}>
        <PlIcon name="qr" size={14} />
        QR label
      </button>
      <button type="button" className="vp-idchip" onClick={openPreview}>
        <PlIcon name="cert" size={14} />
        Certificate
      </button>
    </div>
  );

  const thumbsEl = (
    <div className="vp-thumbs" aria-label="Photo views">
      {slots.map((slot) => {
        const row = firstOfView(slot);
        return row ? (
          <button key={slot} type="button" className="vp-thumb" onClick={() => openEditor("photos")} aria-label={`${VIEW_LABEL[slot]} photo`}>
            <span>
              {isVideoRow(row) ? <PlIcon name="eye" size={20} /> : <HKPImage preset="THUMBNAIL" legacyUrl={rowUrl(row)} mediaId={row?.mediaId} alt={VIEW_LABEL[slot]} />}
            </span>
            {VIEW_LABEL[slot]}
          </button>
        ) : (
          <button key={slot} type="button" className="vp-thumb is-miss" onClick={() => openEditor("photos")} aria-label={`Add ${VIEW_LABEL[slot]} photo`}>
            <span>
              <PlIcon name="plus" size={18} />
            </span>
            {VIEW_LABEL[slot]}
          </button>
        );
      })}
    </div>
  );

  const covEl = (
    <div className="vp-cov">
      <span>
        {media.length} {media.length === 1 ? "photo" : "photos"} · {coverageSummary(coverage)}
      </span>
      <a href={`/edit-product/${encodeURIComponent(id)}?step=photos`} onClick={(e) => { e.preventDefault(); openEditor("photos"); }}>
        Add <PlIcon name="right" size={14} />
      </a>
    </div>
  );

  const authRole: string | undefined = (() => {
    try {
      return JSON.parse(window.localStorage.getItem("authUser") || "null")?.users?.role;
    } catch {
      return undefined;
    }
  })();
  const photoEl = raw?._id ? (
    <PhotoUpdatePanel
      productId={raw._id}
      colour={raw?.color}
      status={raw?.photoStatus}
      role={authRole}
      onAddPhoto={() => openEditor("photos")}
      onChanged={() => void load()}
      onLinkMade={() => setShadeKey((k) => k + 1)}
    />
  ) : null;
  // Shade photos sit under the Photos & dye card; hidden while photo updates are off (no photoStatus).
  const shadeEl =
    raw?._id && raw?.photoStatus ? (
      <ShadePhotosPanel
        productId={raw._id}
        role={authRole}
        refreshKey={shadeKey}
        onChanged={(what) => {
          if (what === "main") {
            setCoverFlash(true);
            window.setTimeout(() => setCoverFlash(false), 2400);
          }
          void load();
        }}
      />
    ) : null;

  const quickEl = (
    <div className={`vp-card${wide ? " vp-quick" : ""}`}>
      <button type="button" className="vp-price" onClick={openPrice} aria-label="Edit price">
        <span className="vp-price__main">
          <small>
            Selling price {isChanged(raw, "sellPrice") ? <span className="vp-chg">changed</span> : null}
          </small>
          {formatRupee(view.sellPrice) ? (
            <>
              <b>{formatRupee(view.sellPrice)}</b>
              {isChanged(raw, "sellPrice") && formatRupee(raw.sellPrice) ? <i>{formatRupee(raw.sellPrice)}</i> : null}
              {formatRupee(view.costPrice) ? <span className="vp-price__cost">Cost {formatRupee(view.costPrice)}</span> : null}
            </>
          ) : (
            <span className="is-add">+ Add price</span>
          )}
        </span>
        <PlIcon name="edit" size={18} />
      </button>
      <div className="vp-stockrow">
        <span className="vp-stockrow__text">
          In stock
          <small>{isDraft ? "Turns on when you publish" : "Changes for buyers at once"}</small>
        </span>
        <StockSwitch product={view} onToggle={onStockToggle} />
      </div>
    </div>
  );

  const draftLeftEl = isDraft ? (
    <button type="button" className="vp-left" onClick={() => openEditor("review")}>
      <span className="vp-dot is-red" />
      <span className="vp-left__text">
        Not visible to buyers yet
        <small>Publish opens the review step: it shows what is left, then puts the piece live</small>
      </span>
      <PlIcon name="right" size={16} />
    </button>
  ) : null;

  const basicsEl = (
    <div className="vp-card">
      <div className="vp-card__title">Basics</div>
      <div className="vp-rows">
        {BASIC_FIELDS.slice(0, 1).map((spec) => (
          <FieldRow key={spec.key} spec={spec} value={displayValue(view, spec)} raw={rawValue(view, spec.key)} changed={isChanged(raw, spec.key)} liveValue={rawValue(raw, spec.key)} onSave={rowSaver(spec)} />
        ))}
        <FieldRow
          spec={{ key: "category", label: "Category", kind: "text" }}
          value={categoryPathText(view)}
          raw=""
          changed={["artform", "type", "category", "subcategory", "weavetype"].some((k) => isChanged(raw, k))}
          liveValue={categoryPathText(raw)}
          onSave={async () => undefined}
          onOpen={(at) => openSheet("category", at)}
        />
        {BASIC_FIELDS.slice(1).map((spec) => (
          <FieldRow key={spec.key} spec={spec} value={displayValue(view, spec)} raw={rawValue(view, spec.key)} changed={isChanged(raw, spec.key)} liveValue={rawValue(raw, spec.key)} onSave={rowSaver(spec)} />
        ))}
        {typeTagsOn ? (
          <FieldRow
            spec={{ key: "typeTagIds", label: "Type tags", kind: "text" }}
            value={(Array.isArray(view.typeTagNames) ? view.typeTagNames : []).join(", ")}
            raw=""
            changed={isChanged(raw, "typeTagIds")}
            onSave={async () => undefined}
            onOpen={(at) => openSheet("typeTags", at)}
          />
        ) : null}
      </div>
    </div>
  );

  const detailsBody = (
    <>
      {groups.map((g) => (
        <React.Fragment key={g.title}>
          <div className="vp-subsec">{g.title}</div>
          <div className={`vp-rows${laptop ? " is-grid" : ""}`}>
            {g.fields.map((spec) => (
              <FieldRow key={spec.key} spec={spec} value={displayValue(view, spec)} raw={rawValue(view, spec.key)} changed={isChanged(raw, spec.key)} liveValue={rawValue(raw, spec.key)} onSave={rowSaver(spec)} />
            ))}
          </div>
        </React.Fragment>
      ))}
      <div className="vp-subsec">Studio fields</div>
      <a className="vp-link" href={`/edit-product/${encodeURIComponent(id)}?step=details`} onClick={(e) => { e.preventDefault(); openEditor("details"); }}>
        Your studio’s own fields are in the step-by-step editor <PlIcon name="right" size={14} />
      </a>
    </>
  );
  const detailsSub = `${detailsFilled.filled} of ${detailsFilled.total} filled`;

  const makingBody = (
    <>
      {steps.length ? (
        <div>
          {steps.map((s) => (
            <div key={s.key} className="vp-step">
              <span className="vp-step__av">{initials(String(s.karigar?.name || s.title || "")) || "·"}</span>
              <span className="vp-step__text">
                {s.title}
                <small>
                  {[s.karigar?.name, s.media?.length ? `${s.media.length} ${s.media.length === 1 ? "photo" : "photos"}` : "no photos"].filter(Boolean).join(" · ")}
                </small>
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="vp-row__hint">No steps yet. Add them as the piece is made; each step can hold an artisan, photos and a capture link.</div>
      )}
      <a className="vp-link" href={`/edit-product/${encodeURIComponent(id)}?step=making`} onClick={(e) => { e.preventDefault(); openEditor("making"); }}>
        Edit steps, artisans and capture links <PlIcon name="right" size={14} />
      </a>
    </>
  );
  const makingSub = steps.length ? `${steps.length} ${steps.length === 1 ? "step" : "steps"}` : "Not started";

  const recordText = (
    <>
      {raw?.createdAt ? `Added ${new Date(raw.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}` : "Added date unknown"}
      {editsPending ? ` · ${editsLabel} as draft` : ""}
    </>
  );

  const nameEl = <NameRow raw={raw} view={view} changed={nameChanged} onSave={rowSaver(NAME_FIELD)} />;

  // ── wide-only pieces (Figma VT01 / VL01) ──
  const stripEl = editsPending ? (
    <div className="vp-strip" role="status">
      <b>{editsLabel}</b>
      <span>saved as draft · buyers see the live version</span>
      <button type="button" className="vp-strip__link" onClick={() => setPublishOpen(true)}>
        Review &amp; publish <PlIcon name="right" size={14} />
      </button>
    </div>
  ) : null;

  const unpublishedEl = editsPending ? (
    <div className="vp-card vp-panel">
      <div className="vp-panel__head">
        <span className="vp-card__title">Unpublished changes</span>
        <span className="pl-badge is-edits">{changes.length}</span>
        <button type="button" className="pl-btn is-sm" onClick={() => setPublishOpen(true)}>
          Review
        </button>
      </div>
      <dl className="vp-diff">
        {changes.map((c) => (
          <div key={c.key}>
            <dt>{c.label}</dt>
            <dd>
              {c.from ? <s>{c.from}</s> : null}
              <b>{c.to}</b>
            </dd>
          </div>
        ))}
      </dl>
    </div>
  ) : null;

  const readyEl = (
    <div className="vp-card vp-panel">
      <div className="vp-panel__head">
        <span className="vp-card__title">Good to have</span>
      </div>
      {missingViews.length ? (
        <>
          <div className="vp-subsec">Photo views · not required</div>
          {missingViews.map((slot) => (
            <div key={slot} className="vp-good">
              <span className="vp-good__dash" aria-hidden="true">
                —
              </span>
              <span>{VIEW_LABEL[slot]}</span>
              <button type="button" className="pl-btn is-ghost is-sm" onClick={() => openEditor("photos")}>
                Add
              </button>
            </div>
          ))}
        </>
      ) : (
        <div className="vp-row__hint">Every photo view is covered.</div>
      )}
    </div>
  );

  const headEl = (
    <div className="vp-head">
      <button type="button" className="pl-iconbtn" aria-label="Back to products" onClick={goBack}>
        <PlIcon name="back" />
      </button>
      <div className="vp-head__title">
        <b>{pieceSn(view)}</b>
        <small>{wide ? [sub, view.name].filter(Boolean).join(" · ") : sub}</small>
      </div>
      {badge ? <span className={`pl-badge is-${badge.kind}`}>{badge.label}</span> : null}
      {wide ? (
        <button type="button" className="pl-btn is-sm" onClick={openPreview}>
          <PlIcon name="eye" size={18} />
          Preview as buyer
        </button>
      ) : null}
      <button type="button" className={`pl-iconbtn${wide ? "" : " is-plain"}`} aria-label="More actions" onClick={(e) => openSheet("more", wide ? rectOf(e) : null)}>
        <PlIcon name="more" />
      </button>
      {wide && editsPending ? (
        <button type="button" className="pl-btn is-primary is-sm" onClick={() => setPublishOpen(true)}>
          Publish changes <span className="vp-head__count">{changes.length}</span>
        </button>
      ) : null}
      {wide && isDraft ? (
        <button type="button" className="pl-btn is-primary is-sm" onClick={() => openEditor("review")}>
          Publish
        </button>
      ) : null}
    </div>
  );

  const labelStripEl = labelToken ? <ProductLabelStrip token={labelToken} productUniqueId={id} /> : null;

  const alertEl = loadError ? (
    <div className="pl-alert is-warn" role="alert">
      <PlIcon name="warn" size={18} />
      <span>{loadError}</span>
    </div>
  ) : null;

  const sheetsEl = (
    <>
      <PublishChangesSheet open={publishOpen} changes={changes} unsavedOnScreen={false} busy={publishing} onKeep={() => setPublishOpen(false)} onPublish={() => void publish()} />
      <ProductQrBindingPanel product={qrOpen ? view : null} open={qrOpen} onClose={() => setQrOpen(false)} onSaved={() => void load()} />

      {host("more", "Actions", 380, (close) => (
        <>
          <div className="vp-sheet__head">
            <div className="vp-sheet__title">
              {view.name}
              <small>
                {pieceSn(view)} · {badge?.label}
              </small>
            </div>
            <button type="button" className="pl-iconbtn is-plain" aria-label="Close" onClick={close}>
              <PlIcon name="x" />
            </button>
          </div>
          <div className="vp-sheet__body">
            <div className="vp-menu">
              <button type="button" onClick={() => { close(); openPreview(); }}>
                <PlIcon name="eye" />
                <span className="vp-menu__text">
                  Preview as buyer<small>The public product page</small>
                </span>
                <PlIcon name="right" size={16} />
              </button>
              <button type="button" onClick={() => void copyLink(close)}>
                <PlIcon name="copy" />
                <span className="vp-menu__text">
                  Copy product link<small>{studioPieceQrUrl(id).replace(/^https?:\/\//, "")}</small>
                </span>
              </button>
              <button type="button" onClick={() => { close(); setQrOpen(true); }}>
                <PlIcon name="qr" />
                <span className="vp-menu__text">
                  QR label<small>{view.xuid ? `HKP tag bound · ${view.xuid}` : "Bind a pre-printed QR label"}</small>
                </span>
                <PlIcon name="right" size={16} />
              </button>
              {editsPending ? (
                <button type="button" onClick={() => { close(); setPublishOpen(true); }}>
                  <PlIcon name="check" />
                  <span className="vp-menu__text">
                    Publish {changes.length} {changes.length === 1 ? "change" : "changes"}<small>Buyers still see the live version</small>
                  </span>
                </button>
              ) : null}
              {isDraft ? (
                <button type="button" onClick={() => { close(); openEditor("review"); }}>
                  <PlIcon name="check" />
                  <span className="vp-menu__text">
                    Publish this piece<small>Shows what is left, then puts it live</small>
                  </span>
                  <PlIcon name="right" size={16} />
                </button>
              ) : null}
              <button type="button" onClick={() => { close(); openEditor(); }}>
                <PlIcon name="edit" />
                <span className="vp-menu__text">
                  Open the step-by-step editor<small>Photos, making, every field, catalogue and transfer</small>
                </span>
                <PlIcon name="right" size={16} />
              </button>
            </div>
            <div className="vp-row__hint">Add to catalogue, transfer, new colour and archive stay in the product list’s ⋯ for now.</div>
          </div>
        </>
      ))}

      {host("category", "Category", 440, (close) => (
        <>
          <div className="vp-sheet__head">
            <div className="vp-sheet__title">
              Category<small>One search fills all five levels · saves only this field</small>
            </div>
            <button type="button" className="pl-iconbtn is-plain" aria-label="Close" onClick={close}>
              <PlIcon name="x" />
            </button>
          </div>
          <div className="vp-sheet__body ps-shell">
            <CategoryPathPicker
              value={{ artform: view.artform, type: view.type, category: view.category, subCategory: view.subcategory, weaveType: view.weavetype }}
              levelsOpen={false}
              onShowLevels={() => { close(); openEditor("basics"); }}
              onPick={(path) => {
                void saveFields({
                  artformId: path.artform.value,
                  typeId: path.type.value,
                  categoryId: path.category.value,
                  subcategoryId: path.subCategory.value,
                  // A path without a weave keeps the piece's current weave (a live piece may not lose it).
                  weavetypeId: path.weaveType?.value || masterIdFromRef(view.weavetype) || "",
                })
                  .then(() => {
                    close();
                    return load();
                  })
                  .catch((e: any) => notifications({ title: "Error", message: e?.message || "Could not save.", type: "danger" }));
              }}
            />
          </div>
        </>
      ))}

      {host("typeTags", "Type tags", 440, (close) => (
        <>
          <div className="vp-sheet__head">
            <div className="vp-sheet__title">
              Type tags<small>Saves only this field</small>
            </div>
            <button type="button" className="pl-iconbtn is-plain" aria-label="Close" onClick={close}>
              <PlIcon name="x" />
            </button>
          </div>
          <div className="vp-sheet__body ps-shell">
            <TypeTagPicker value={typeTagIds} onChange={setTypeTagIds} suggestFrom={{ name: view.name, subcategoryName: view.subcategory?.name }} />
            <div className="vp-row__actions">
              <button type="button" className="pl-btn" onClick={close}>
                Cancel
              </button>
              <button
                type="button"
                className="pl-btn is-primary is-wide"
                onClick={() =>
                  void saveFields({ typeTagIds })
                    .then(() => close())
                    .catch((e: any) => notifications({ title: "Error", message: e?.message || "Could not save.", type: "danger" }))
                }
              >
                Save
              </button>
            </div>
          </div>
        </>
      ))}

      {host("price", "Price", 420, (close) => (
        <>
          <div className="vp-sheet__head">
            <div className="vp-sheet__title">
              Price
              <small>
                {view.priceEditsRequirePublish ? "Kept as a draft edit until you publish" : isDraft ? "Shown when the piece is published" : "Changes for buyers when you save"}
              </small>
            </div>
            <button type="button" className="pl-iconbtn is-plain" aria-label="Close" onClick={close}>
              <PlIcon name="x" />
            </button>
          </div>
          <div className="vp-sheet__body">
            <div className="vp-field">
              <label htmlFor="vp-sell">Selling price ₹</label>
              <input id="vp-sell" className="vp-input" inputMode="decimal" value={price.sell} onChange={(e) => setPrice((p) => ({ ...p, sell: filterPriceInput(e.target.value) }))} placeholder="e.g. 55000" autoFocus={!wide} />
            </div>
            <div className="vp-field">
              <label htmlFor="vp-cost">
                Cost price ₹ <i>only your team sees</i>
              </label>
              <input id="vp-cost" className="vp-input" inputMode="decimal" value={price.cost} onChange={(e) => setPrice((p) => ({ ...p, cost: filterPriceInput(e.target.value) }))} placeholder="optional" />
            </div>
            <div className="vp-row__actions">
              <button type="button" className="pl-btn" onClick={close} disabled={priceBusy}>
                Cancel
              </button>
              <button type="button" className="pl-btn is-primary is-wide" onClick={() => void savePrice(close)} disabled={priceBusy}>
                {priceBusy ? "Saving…" : "Save price"}
              </button>
            </div>
          </div>
        </>
      ))}
    </>
  );

  // ── tablet and laptop (VT01 / VL01): photos left, every section open, Publish in the top bar ──
  if (wide) {
    const detailsCard = (
      <div className="vp-card">
        <div className="vp-card__title vp-card__title--sub">
          Details <small>· {detailsSub} · a pencil changes one row</small>
        </div>
        {detailsBody}
      </div>
    );
    const makingCard = (
      <div className="vp-card">
        <div className="vp-card__title vp-card__title--sub">
          Making <small>· {makingSub}</small>
        </div>
        {makingBody}
      </div>
    );
    const recordCard = (
      <div className="vp-card vp-record">
        <PlIcon name="clock" size={18} />
        <span className="vp-record__text">
          Record<small>{recordText}</small>
        </span>
      </div>
    );
    return (
      <div className={`pl-page vp-page is-${layout}`}>
        {headEl}
        <div className="vp-wide">
          <div className="vp-wide__photos">
            {coverEl}
            {covEl}
            {thumbsEl}
          </div>
          <div className="vp-wide__main">
            {alertEl}
            {nameEl}
            {idsEl}
            {labelStripEl}
            {stripEl}
            {draftLeftEl}
            {laptop ? (
              <div className="vp-wide__cols is-laptop">
                <div className="vp-wide__col">
                  {quickEl}
                  {basicsEl}
                  {detailsCard}
                </div>
                <aside className="vp-wide__panel" aria-label="Changes, readiness, making and record">
                  {photoEl}
                  {shadeEl}
                  {unpublishedEl}
                  {readyEl}
                  {makingCard}
                  {recordCard}
                </aside>
              </div>
            ) : (
              <div className="vp-wide__cols">
                <div className="vp-wide__col">
                  {quickEl}
                  {photoEl}
                  {shadeEl}
                  {basicsEl}
                  {makingCard}
                  {recordCard}
                </div>
                <div className="vp-wide__col">{detailsCard}</div>
              </div>
            )}
          </div>
        </div>
        {sheetsEl}
      </div>
    );
  }

  // ── phone (V01): one column, Details and Making as accordions, sticky Publish bar ──
  return (
    <div className={`pl-page vp-page is-phone${editsPending || isDraft ? " has-foot" : ""}`}>
      {headEl}

      <div className="vp-body">
        {alertEl}

        <div className="vp-hero">
          {coverEl}
          <div className="vp-hero__side">
            {nameEl}
            {idsEl}
          </div>
        </div>

        {labelStripEl}
        {thumbsEl}
        {covEl}
        {quickEl}
        {photoEl}
        {shadeEl}
        {draftLeftEl}
        {basicsEl}

        <div className="vp-acc">
          <button type="button" className={`vp-acc__head${detailsOpen ? " is-open" : ""}`} aria-expanded={detailsOpen} onClick={() => setDetailsOpen((o) => !o)}>
            <PlIcon name="loom" />
            <span className="vp-acc__text">
              Details
              <small>
                {detailsSub}
                {detailsOpen ? " · tap a pencil to change one" : ""}
              </small>
            </span>
            <PlIcon name="down" size={18} className="is-chev" />
          </button>
          {detailsOpen ? <div className="vp-acc__body">{detailsBody}</div> : null}
        </div>

        <div className="vp-acc">
          <button type="button" className={`vp-acc__head${makingOpen ? " is-open" : ""}`} aria-expanded={makingOpen} onClick={() => setMakingOpen((o) => !o)}>
            <PlIcon name="loom" />
            <span className="vp-acc__text">
              Making
              <small>{makingSub}</small>
            </span>
            <PlIcon name="down" size={18} className="is-chev" />
          </button>
          {makingOpen ? <div className="vp-acc__body">{makingBody}</div> : null}
        </div>

        <div className="vp-acc">
          <div className="vp-acc__head" style={{ cursor: "default" }}>
            <PlIcon name="clock" />
            <span className="vp-acc__text">
              Record
              <small>{recordText}</small>
            </span>
          </div>
        </div>
      </div>

      {editsPending ? (
        <div className="vp-foot">
          <span className="vp-foot__text">
            <b>{editsLabel}</b> saved as draft · buyers see the live version
          </span>
          <button type="button" className="pl-btn is-primary" onClick={() => setPublishOpen(true)}>
            Publish changes
          </button>
        </div>
      ) : null}
      {isDraft ? (
        <div className="vp-foot">
          <span className="vp-foot__text">
            <b>Draft</b> · not visible to buyers yet
          </span>
          <button type="button" className="pl-btn is-primary" onClick={() => openEditor("review")}>
            Publish
          </button>
        </div>
      ) : null}

      {sheetsEl}
    </div>
  );
};

/** The name sits beside the cover (phone) or above the fields (wide); its pencil edits it in place like any other row. */
const NameRow = ({ raw, view, changed, onSave }: { raw: any; view: any; changed: boolean; onSave: (text: string) => Promise<void> }) => (
  <div className="vp-name">
    <div style={{ flex: 1, minWidth: 0 }}>
      <FieldRow spec={NAME_FIELD} value={String(view?.name || "")} raw={String(view?.name || "")} changed={changed} liveValue={String(raw?.name || "")} onSave={onSave} />
    </div>
  </div>
);

export default ViewProductPage;
